<template>
  <!-- Extra container needed for custom classes -->
  <div class="tw-flex tw-flex-col">
    <div
      class="tw-relative tw-flex tw-flex-grow tw-flex-col tw-items-center tw-justify-center"
      :class="heightClass"
    >
      <ClientOnly>
        <Map
          class="tw-h-full tw-w-full tw-flex-grow tw-rounded-t-lg"
          :map-style="mapStyle"
          :options="{
            zoom: 5,
            center: mapCenter
          }"
        >
          <template #default="{ map }">
            <template v-if="polylineCoordinates.length">
              <MapCenter
                :duration="1000"
                :points="polylineCoordinates"
                :map="map"
                :zoom="30"
                @on-zoom-end="state.mapAnimationComplete = true"
              />

              <MapRoute
                :coordinates="polylineCoordinates"
                :map="map"
                :uses-shipped-route-source="usesShippedRouteSource"
                :interactive="false"
                line-color="#3e8dba"
                :line-width="4"
              />

              <!-- Start marker -->
              <MapMarker
                :position="polylineCoordinates[0]"
                :map="map"
                :el="startMarker"
                :options="{ draggable: false }"
              />

              <!-- End marker -->
              <MapMarker
                :position="polylineCoordinates[polylineCoordinates.length - 1]"
                :map="map"
                :el="endMarker"
                :options="{ draggable: false }"
              />
            </template>

            <!-- Image markers -->
            <MapMarker
              v-for="(photo, index) in sectionPhotos"
              :key="photo.id"
              :position="[photo.longitude, photo.latitude]"
              :map="map"
              :el="photoMarkers[index]"
              :options="{ draggable: false, color: '#222' }"
              @click="photoClicked(index, photo.id)"
            />
          </template>
        </Map>
      </ClientOnly>

      <slot />
    </div>

    <!-- Start / end marker elements (moved onto the map by MapMarker) -->
    <div class="tw-hidden">
      <div
        ref="startMarker"
        class="tw-h-4 tw-w-4 tw-rounded-full tw-border-2 tw-border-white tw-bg-riser-green tw-shadow-md"
      />
      <div
        ref="endMarker"
        class="tw-h-4 tw-w-4 tw-rounded-full tw-border-2 tw-border-white tw-bg-riser-red tw-shadow-md"
      />
    </div>

    <!-- Hidden image marker elements -->
    <div
      v-for="photo in sectionPhotos"
      :key="photo.id"
      ref="photoMarkers"
      :style="{ backgroundImage: `url(${photo.thumb})` }"
      class="tw-h-11 tw-w-11 tw-cursor-pointer tw-rounded tw-bg-white tw-bg-cover tw-bg-center tw-shadow-lg tw-transition-opacity tw-duration-1000"
      :class="[
        state.mapAnimationComplete ? 'tw-static tw-opacity-100' : 'tw-absolute tw-opacity-0'
      ]"
    />
  </div>
</template>

<script setup lang="ts">
import polyline from '@mapbox/polyline'

import type { MapStyleKey } from '~/composables/useMapStyles'

const emit = defineEmits(['openImage', 'openImageId'])

const props = withDefaults(
  defineProps<{
    sectionPolyline?: string
    sectionPhotos: any[]
    mapStyleKey?: MapStyleKey
    heightClass?: string
  }>(),
  {
    sectionPolyline: undefined,
    mapStyleKey: 'street',
    heightClass: 'tw-h-[400px]'
  }
)

const { getStyle, getStyleUrl } = useMapStyles()

const mapStyle = computed(() => getStyleUrl(props.mapStyleKey))

const usesShippedRouteSource = computed(() => getStyle(props.mapStyleKey).usesShippedRouteSource)

const photoMarkers = ref<HTMLDivElement[]>([])
const startMarker = ref<HTMLDivElement>()
const endMarker = ref<HTMLDivElement>()

const state = reactive({
  mapAnimationComplete: false
})

const polylineCoordinates = computed<[number, number][]>(() => {
  if (!props.sectionPolyline) {
    return []
  }

  return polyline
    .decode(props.sectionPolyline, 6)
    .map((coordinate) => [coordinate[1], coordinate[0]])
})

const mapCenter = computed(() => {
  const [first] = polylineCoordinates.value

  return first ? { lat: first[1], lng: first[0] } : { lat: 0, lng: 0 }
})

const photoClicked = (index: number, photoId: number) => {
  emit('openImage', index)
  emit('openImageId', photoId)
}
</script>
